import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { trackEvent } from '../utils/posthog';

interface Contact {
  contact_id: number;
  name: string;
  email: string | null;
  contact_type: string;
  is_user: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export default function MojiLudiaPage() {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    trackEvent('contacts_page_viewed', {
      page: 'MojiLudiaPage',
      section: 'contacts_management'
    });
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = async () => {
    setIsLoading(true);
    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/getContacts`, {
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!formName.trim()) {
      setSaveMessage('❌ Meno je povinné');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setSaveMessage('❌ Chyba autentifikácie');
        setIsSaving(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/addContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formName.trim(),
          email: formEmail.trim() || null,
          contactType: 'split'
        })
      });

      if (response.ok) {
        setSaveMessage('✓ Kontakt pridaný');
        trackEvent('contact_added', {
          hasEmail: !!formEmail.trim(),
          source: 'manual'
        });
        setFormName('');
        setFormEmail('');
        setShowAddForm(false);
        await loadContacts();
      } else {
        const error = await response.json();
        setSaveMessage(`❌ ${error.error || 'Nepodarilo sa pridať'}`);
        trackEvent('contact_add_failed', {
          error: error.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Failed to add contact:', error);
      setSaveMessage('❌ Chyba pri pridávaní');
      trackEvent('contact_add_failed', {
        error: 'Network or client error'
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleUpdateContact = async (contactId: number) => {
    if (!formName.trim()) {
      setSaveMessage('❌ Meno je povinné');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setSaveMessage('❌ Chyba autentifikácie');
        setIsSaving(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/updateContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({
          contactId,
          name: formName.trim(),
          email: formEmail.trim() || null
        })
      });

      if (response.ok) {
        setSaveMessage('✓ Kontakt upravený');
        trackEvent('contact_updated', {
          contactId,
          hasEmail: !!formEmail.trim()
        });
        setEditingContactId(null);
        setFormName('');
        setFormEmail('');
        await loadContacts();
      } else {
        const error = await response.json();
        setSaveMessage(`❌ ${error.error || 'Nepodarilo sa upraviť'}`);
        trackEvent('contact_update_failed', {
          error: error.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Failed to update contact:', error);
      setSaveMessage('❌ Chyba pri upravovaní');
      trackEvent('contact_update_failed', {
        error: 'Network or client error'
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleDeleteContact = async (contactId: number, name: string) => {
    if (!window.confirm(`Naozaj chceš odstrániť kontakt "${name}"?\n\nToto neovplyvní staré rozdelenia bločkov.`)) {
      return;
    }

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setSaveMessage('❌ Chyba autentifikácie');
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/deleteContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({ contactId })
      });

      if (response.ok) {
        setSaveMessage('✓ Kontakt odstránený');
        trackEvent('contact_deleted', {
          contactId,
          name
        });
        await loadContacts();
      } else {
        const error = await response.json();
        setSaveMessage(`❌ ${error.error || 'Nepodarilo sa odstrániť'}`);
        trackEvent('contact_delete_failed', {
          error: error.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
      setSaveMessage('❌ Chyba pri odstraňovaní');
      trackEvent('contact_delete_failed', {
        error: 'Network or client error'
      });
    } finally {
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const startEdit = (contact: Contact) => {
    trackEvent('edit_contact_button_clicked', {
      contactId: contact.contact_id,
      hasEmail: !!contact.email
    });
    setEditingContactId(contact.contact_id);
    setFormName(contact.name);
    setFormEmail(contact.email || '');
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingContactId(null);
    setFormName('');
    setFormEmail('');
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
        <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '60px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
          👥 Ľudia, s ktorými rozdeľuješ bločky
        </h1>
        <div style={{ height: '100px', background: '#f9fafb', borderRadius: '12px', marginTop: '24px' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/profile/moje-skupiny')}
        style={{
          background: 'none',
          border: 'none',
          color: '#667eea',
          fontSize: '15px',
          fontWeight: 500,
          cursor: 'pointer',
          marginBottom: '16px',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Späť
      </button>

      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
        👥 Ľudia, s ktorými rozdeľuješ bločky
      </h1>
      <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
        Spravuj zoznam ľudí, s ktorými najčastejšie rozdeľuješ bločky. Úpravy sa uplatnia len pre budúce rozdelenia, už vytvorené rozdelenia ostávajú bez zmeny. Ak pri rozdeľovaní zadáš údaje nového človeka, automaticky sa pridá do tohto zoznamu. Poradie ľudí pri rozdeľovaní sa automaticky zoraďuje podľa početnosti a aktuálnosti.
      </p>

      {/* Save message */}
      {saveMessage && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          background: saveMessage.startsWith('✓') ? '#d1fae5' : '#fee2e2',
          color: saveMessage.startsWith('✓') ? '#059669' : '#dc2626',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {saveMessage}
        </div>
      )}

      {/* Add button */}
      {!showAddForm && !editingContactId && (
        <button
          onClick={() => {
            trackEvent('add_contact_button_clicked');
            setShowAddForm(true);
          }}
          style={{
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ➕ Pridať osobu
        </button>
      )}

      {/* Add form */}
      {showAddForm && (
        <div style={{
          background: '#f9fafb',
          border: '2px solid #667eea',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
            Pridať novú osobu
          </h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
              Meno *
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Napr. Janka"
              maxLength={255}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
              Email (voliteľné)
            </label>
            <input
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              placeholder="Napr. janka@example.com"
              maxLength={255}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleAddContact}
              disabled={isSaving || !formName.trim()}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isSaving || !formName.trim() ? 'not-allowed' : 'pointer',
                opacity: isSaving || !formName.trim() ? 0.5 : 1
              }}
            >
              {isSaving ? 'Ukladám...' : 'Pridať'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setFormName('');
                setFormEmail('');
              }}
              disabled={isSaving}
              style={{
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer'
              }}
            >
              Zrušiť
            </button>
          </div>
        </div>
      )}

      {/* Contacts list */}
      {contacts.length === 0 ? (
        <div style={{
          background: '#f9fafb',
          borderRadius: '12px',
          padding: '40px 20px',
          textAlign: 'center',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
            Zatiaľ tú nie sú žiadni ľudia
          </p>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            Môžeš si pridať ľudí, s ktorými plánuješ rozdeľovať bločky.
          </p>
        </div>
      ) : (
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {contacts.map((contact, index) => (
            <div
              key={contact.contact_id}
              style={{
                borderBottom: index < contacts.length - 1 ? '1px solid #e5e7eb' : 'none'
              }}
            >
              {editingContactId === contact.contact_id ? (
                // Edit form
                <div style={{ padding: '16px', background: '#f9fafb' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Meno *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      maxLength={255}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Email (voliteľné)
                    </label>
                    <input
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      maxLength={255}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleUpdateContact(contact.contact_id)}
                      disabled={isSaving || !formName.trim()}
                      style={{
                        background: '#667eea',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: isSaving || !formName.trim() ? 'not-allowed' : 'pointer',
                        opacity: isSaving || !formName.trim() ? 0.5 : 1
                      }}
                    >
                      {isSaving ? 'Ukladám...' : 'Uložiť'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={isSaving}
                      style={{
                        background: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: isSaving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Zrušiť
                    </button>
                  </div>
                </div>
              ) : (
                // Contact display
                <div style={{
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginBottom: '2px' }}>
                      {contact.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {contact.email || 'Bez e-mailu'}
                    </div>
                    {contact.usage_count > 0 && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        Použité {contact.usage_count}× pri rozdeľovaní
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => startEdit(contact)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px 8px',
                        color: '#667eea'
                      }}
                      title="Upraviť"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteContact(contact.contact_id, contact.name)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px 8px',
                        color: '#dc2626'
                      }}
                      title="Odstrániť"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
